import { useEffect, useRef } from 'react';
import { useTypewriter } from '@/hooks/useTypewriter';


const TYPEWRITER_WORDS = [
  'comunica',
  'conecta',
  'convierte',
  'vende',
  'impacta',
  'posiciona',
  'destaca',
  'transmite',
  'atrae',
  'fideliza',
  'persuade',
  'cautiva',
  'impulsa',
  'construye marca',
  'genera valor',
  'marca diferencia',
  'eleva tu marca',
  'transforma ideas',
  'crea identidad',
  'deja huella',
];

export default function Hero() {
  const titleRef = useRef<HTMLHeadingElement>(null);
  const { displayWord } = useTypewriter({
    words: TYPEWRITER_WORDS,
    typingSpeed: 70,
    deletingSpeed: 40,
    pauseDuration: 1000,
  });

  useEffect(() => {
    const el = titleRef.current;
    if (el) {
      setTimeout(() => el.classList.add('visible'), 200);
    }
  }, []);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden bg-black"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://static.readdy.ai/image/7b80f9c748a6c492c1a0f4fbaf2d66d7/bb38dcc972d35f423ba12355923ab584.png"
          alt="Hero background"
          className="w-full h-full object-cover object-center opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/60 to-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/80"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-transparent to-black/80"></div>
      </div>



      {/* Floating geometric shapes */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="animate-float absolute top-[15%] right-[8%] w-64 h-64 rounded-full bg-[#FF6B35]/10 blur-3xl"></div>
        <div className="animate-float-delay absolute top-[40%] right-[20%] w-48 h-48 rounded-full bg-[#00D9FF]/10 blur-3xl"></div>
        <div className="animate-float-slow absolute bottom-[20%] right-[5%] w-80 h-80 rounded-full bg-[#FF006E]/10 blur-3xl"></div>
        <div className="animate-float absolute top-[60%] left-[5%] w-40 h-40 rounded-full bg-[#FFD60A]/8 blur-2xl"></div>

        {/* Geometric lines */}
        <div className="animate-float absolute top-[20%] right-[15%] w-32 h-32 border border-[#FF6B35]/20 rounded-lg rotate-45"></div>
        <div className="animate-float-delay absolute top-[55%] right-[30%] w-20 h-20 border border-[#00D9FF]/20 rounded-full"></div>
        <div className="animate-float-slow absolute bottom-[30%] right-[10%] w-16 h-16 border border-[#FF006E]/20 rotate-12"></div>
      </div>

      {/* Content */}
      <div className="relative z-[3] max-w-7xl mx-auto px-6 pt-24 pb-16 w-full">
        <div className="max-w-3xl">
          {/* Badge */}
          <div className="fade-in-up visible inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2 mb-8">
            <span className="w-2 h-2 rounded-full bg-[#06FFA5] animate-pulse"></span>
            <span className="text-sm text-white/70 font-medium">Disponible para proyectos</span>
          </div>

          {/* Name */}
          <h1
            ref={titleRef}
            className="fade-in-up font-bebas text-6xl sm:text-7xl md:text-8xl lg:text-9xl leading-none tracking-wide text-white mb-2"
          >
            ANDRÉS
            <br />
            <span className="gradient-text">CADENA</span>
          </h1>

          {/* Typewriter */}
          <div className="fade-in-up visible mb-5">
            <span className="font-poppins text-xl md:text-2xl font-light text-white/75 tracking-wide">
              Diseño que{' '}
              <span className="font-semibold text-[#FF6B35]">
                {displayWord}
              </span>
              <span className="inline-block w-[2px] h-[1.1em] bg-[#FF6B35]/70 ml-[2px] align-middle animate-blink"></span>
            </span>
          </div>

          {/* Role */}
          <p className="fade-in-up text-lg md:text-xl text-white/60 font-poppins font-light tracking-widest uppercase mb-8">
            Diseñador Gráfico &amp; Publicista — Bogotá, Colombia
          </p>

          {/* Tagline */}
          <p className="fade-in-up text-xl md:text-2xl lg:text-3xl text-white/90 font-poppins font-light leading-relaxed mb-12 max-w-2xl">
            Diseño que{' '}
            <strong className="font-semibold text-[#FF6B35]">comunica</strong>,{' '}
            <strong className="font-semibold text-[#00D9FF]">conecta</strong> y convierte
            ideas en{' '}
            <strong className="font-semibold text-[#06FFA5]">resultados reales</strong>
          </p>

          {/* CTAs */}
          <div className="fade-in-up flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => handleScroll('#portfolio')}
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF6B35] to-[#FF006E] hover:from-[#e55a25] hover:to-[#d4005c] text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 text-base whitespace-nowrap cursor-pointer"
            >
              <i className="ri-eye-line"></i>
              Ver portafolio
            </button>
            <button
              onClick={() => handleScroll('#contact')}
              className="flex items-center justify-center gap-2 border-2 border-white/40 hover:border-white text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:bg-white/10 text-base whitespace-nowrap cursor-pointer"
            >
              <i className="ri-send-plane-line"></i>
              Contáctame
            </button>
          </div>

          {/* Stats */}
          <div className="fade-in-up flex flex-wrap gap-8 mt-16 pt-8 border-t border-white/10">
            {[
              { value: '5+', label: 'Años de experiencia' },
              { value: '100+', label: 'Proyectos entregados' },
              { value: '50+', label: 'Clientes satisfechos' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-bebas text-4xl text-[#FF6B35]">{stat.value}</p>
                <p className="text-sm text-white/50 font-poppins">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-xs text-white/40 tracking-widest uppercase">Scroll</span>
        <i className="ri-arrow-down-line text-white/40 text-xl"></i>
      </div>
    </section>
  );
}
